import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { t } from '@lingui/macro'
import { useEffect, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H1 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { LoginLayout } from '@/modules/auth/components/login/login-layout/LoginLayout'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { NativeStoreButton } from '@/modules/shared/components/native-store-button/NativeStoreButton'
import { useCheckVersion } from '../../hooks/useCheckVersion'
import { RequiredVersionContent, RequiredVersionContentContainer } from './RequiredVersion.page.styled'

import type { AppInfo as CapacitorAppInfo } from '@capacitor/app'
import type { FC, ReactNode } from 'react'

declare const APP_VERSION: string

type RequiredVersionProps = {
  children: ReactNode
}

const RequiredVersion: FC<RequiredVersionProps> = ({ children }) => {
  const theme = useTheme()
  const [info, setInfo] = useState<CapacitorAppInfo>()
  const isNative = Capacitor.isNativePlatform()

  const {
    checkVersion: { execute, value: hasCorrectVersion, isLoading, error },
  } = useCheckVersion()

  const image = '/img/happy-nurse-zoom.png'
  const imagePosition = '50%'

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (isNative) {
      App.getInfo().then((value) => {
        setInfo(value)
      })
    } else {
      mixpanel.registerProperties({
        app_version: APP_VERSION,
      })
    }
  }, [isNative])

  useEffect(() => {
    if (info?.version) {
      execute(info.version)
    }
  }, [execute, info, info?.version])

  useEffect(() => {
    if (info?.build && info?.version) {
      mixpanel.registerProperties({
        app_version: info?.version,
        app_build: info?.build,
      })
    }
  }, [info?.version, info?.build])

  const isBlocked = !isLoading && hasCorrectVersion === false && isNative && !error
  const trackedVersionGate = useRef(false)
  useEffect(() => {
    if (isBlocked && !trackedVersionGate.current) {
      trackedVersionGate.current = true
      mixpanel.track('blocking_admin_message_show', {
        message_type: 'version_gate',
        platform: Capacitor.getPlatform(),
      })
    }
  }, [isBlocked])

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  // Pass through when the check fails (network/API error) — don't block
  // app usage because the version endpoint is unreachable
  return isLoading ? (
    <AnimatedLoader cancellable />
  ) : hasCorrectVersion || !isNative || error ? (
    <>{children}</>
  ) : (
    <LoginLayout image={image} imagePosition={imagePosition}>
      <RequiredVersionContentContainer>
        <RequiredVersionContent gap={theme.UI.Spacing.Space4}>
          <FlexColumn gap={theme.UI.Spacing.Space5}>
            <H1>
              {t({
                id: 'versio-update.title',
                message: 'Welkom bij MijnZiekenhuis',
              })}
            </H1>
            <FlexColumn gap={theme.UI.Spacing.Space4}>
              <Span type='BodyLargeRegular'>
                {t({
                  id: 'version-update.description1',
                  message: 'Je hebt een verouderde versie van de app die niet meer ondersteund wordt.',
                })}
              </Span>
              <Span type='BodyLargeRegular'>
                {t({
                  id: 'version-update.description2',
                  message: 'Gelieve up te daten via de {store}',
                  values: {
                    store: Capacitor.getPlatform() === 'android' ? 'Google Play Store' : 'App Store',
                  },
                })}
              </Span>

              <Flex justifyContent='center'>
                {Capacitor.getPlatform() === 'android' ? (
                  <NativeStoreButton
                    type='playStore'
                    url='https://play.google.com/store/apps/details?id=be.mariamiddelares.app'
                  />
                ) : (
                  <NativeStoreButton type='appStore' url='https://apps.apple.com/us/app/mijnziekenhuis/id1634606301' />
                )}
              </Flex>
            </FlexColumn>
          </FlexColumn>
        </RequiredVersionContent>
      </RequiredVersionContentContainer>
    </LoginLayout>
  )
}

export { RequiredVersion }
